<script setup lang="ts">
	import { useDebounceFn } from '@vueuse/core'
	import { computed, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledDropZone from '@/components/StyledDropZone.vue'
	import StyledImagePreview from '@/components/StyledImagePreview.vue'

	import { Media } from '@/interfaces/media'

	import type { UserDocumentsForm } from '@/modules/users/pages/partials/edit/DocumentsTab.vue'

	import HasPermission from './HasPermission.vue'
	import StyledButton from './StyledButton.vue'

	interface Props {
		mediaKey: keyof UserDocumentsForm
		file: File | null
		expiresAt?: string
		label?: string
		acceptTypes?: string[]
		maxKb?: number
		dropZoneClass?: string
		media?: Media
		fileName?: string
		alt?: string
		description?: string
		saveMedia: (value: keyof UserDocumentsForm) => Promise<void>
		namePermissionCreate?: string
		namePermissionUpdate?: string
		namePermissionDelete?: string
		entityType: string
	}

	interface Emits {
		(event: 'update:file', value: File | null): void
		(event: 'update:expiresAt', value: string): void
		(
			event: 'update:media',
			...args: Parameters<{ (id: number, expiresAt: string): void }>
		): void
		(event: 'delete:media', id: number): void
	}

	const props = withDefaults(defineProps<Props>(), {
		maxKb: 20480,
		dropZoneClass: '',
	})
	const emits = defineEmits<Emits>()
	const newExpiresAt = ref(props.media?.expirationDate ?? '')
	const editingMode = ref(false)
	const toast = useToast()
	const saving = ref(false)
	const error = ref('')
	const showError = ref(false)

	// Obtener la fecha de hoy en formato YYYY-MM-DD
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const minDate = ref(today)

	// Computed para detectar si hay cambios
	const hasChanges = computed(() => {
		return Boolean(props.file && props.expiresAt)
	})

	const handleSave = useDebounceFn(async () => {
		saving.value = true
		try {
			if (!props.file || !props.expiresAt) {
				error.value =
					!props.file ?
						'No se ha seleccionado un archivo'
					:	'No se ha seleccionado una fecha'
				return toast.error(error.value)
			}

			// Validar que la fecha no sea anterior a hoy
			const selectedDate = new Date(props.expiresAt)
			const todayDate = new Date()
			todayDate.setHours(0, 0, 0, 0)

			if (selectedDate < todayDate) {
				error.value = 'La fecha de vigencia no puede ser anterior a hoy'
				return toast.error(error.value)
			}

			await props.saveMedia(props.mediaKey)
			//Clear form
			emits('update:file', null)
			emits('update:expiresAt', '')

			editingMode.value = false
		} catch (err) {
			console.log(err)
		} finally {
			saving.value = false
		}
	}, 200)

	watch(error, (value) => {
		if (value) {
			showError.value = true
			useDebounceFn(() =>
				setTimeout(() => {
					showError.value = false
					error.value = ''
				}, 5000)
			)()
		}
	})

	watch(newExpiresAt, (value) => {
		if (
			props.expiresAt ?
				value !== props.expiresAt
			:	props.media?.expirationDate !== value
		) {
			emits('update:media', props.media!.id, value)
		}
	})

	watch(
		() => props.media,
		(value) => {
			if (value) {
				newExpiresAt.value = value.expirationDate
			}
		}
	)
</script>

<template>
	<div
		v-if="!media || editingMode"
		class="flex flex-col gap-4"
	>
		<StyledDropZone
			:modelValue="file"
			@update:modelValue="($event) => $emit('update:file', $event)"
			:acceptTypes="acceptTypes"
			:maxKb="maxKb"
			:drop-zone-class="dropZoneClass"
			:label="label"
		/>

		<p
			v-if="error && showError"
			class="text-red-500 text-sm"
		>
			{{ error }}
		</p>

		<StyledDateInput
			:model-value="expiresAt"
			@update:modelValue="($event) => $emit('update:expiresAt', $event)"
			placeholder="Fecha de vencimiento"
			:min-date="minDate"
			label="Fecha de vencimiento"
		/>

		<HasPermission
			:name-permission="namePermissionCreate || ''"
			:entity-type="entityType"
		>
			<nav class="flex items-center justify-end gap-4">
				<StyledButton
					v-if="media"
					label="Cancelar"
					class="border border-gray-300 hover:bg-gray-50 transition-colors"
					@click="() => (editingMode = !editingMode)"
				/>

				<StyledButton
					label="Guardar"
					:disabled="!hasChanges || saving"
					:class="[
						hasChanges && !saving ?
							'bg-[#253C70] hover:bg-[#1e2f5a] text-white transition-colors'
						:	'bg-gray-300 text-gray-500 cursor-not-allowed',
					]"
					@click="handleSave"
					:loading="saving"
				/>
			</nav>
		</HasPermission>
	</div>

	<div v-else-if="!editingMode">
		<StyledImagePreview
			:expires-at="newExpiresAt"
			:media="media"
			:title="label"
			:alt="alt"
			:fileName="fileName"
			:description="description"
			@toggle:editing-mode="() => (editingMode = !editingMode)"
			@update:model-value="($event) => (newExpiresAt = $event)"
			@delete:media="() => $emit('delete:media', media!.id)"
			:name-permission-delete="namePermissionDelete"
			:name-permission-update="namePermissionUpdate"
			:entity-type="entityType"
		/>
	</div>
</template>

<style scoped></style>
